/* eslint-disable camelcase */
import { useFormik } from 'formik';
import React, { createContext } from 'react';
import ErrorBoundary from '../../ErrorBoundary';
import Form from '../../form-elements/Form';
import Legend from '../../form-elements/Legend';
import Required from '../../form-elements/Required';
import TextField from '../../form-elements/TextField';
import LoadingSpinner from '../../LoadingSpinner';
import useNewsletterForm from '../hooks/useNewsletterForm';

export const FormContext = createContext(null);

const scrollToTop = () => {
  window.scrollTo(0, 0);
};

const NewsletterForm = () => {
  const {
    popupNewsletterInitialValues,
    popupNewsletterValidationSchema,
    disable,
    postForm,
    fields = {},
    waiting,
    handleReCaptchaVerify,
    showSuccessDialog,
    setShowSuccessDialog,
  } = useNewsletterForm();

  const {
    handleBlur,
    touched,
    values,
    errors,
    setFieldValue,
    setValues,
    setTouched,
    submitForm,
  } = useFormik({
    initialValues: popupNewsletterInitialValues,
    validationSchema: popupNewsletterValidationSchema,
    onSubmit: async () => {
      scrollToTop();
      const recaptchaToken = await handleReCaptchaVerify();

      const formData = {
        ...values,
        'g-recaptcha-response': recaptchaToken,
      };

      await postForm(formData, recaptchaToken, () => {
        setValues(popupNewsletterInitialValues);

        setTouched({});

        // post to data layer
        const { first_name, last_name, email, opt_in, current_page, referrer } =
          values;

        // set email for DotDigital WBT
        window.dmPt('identify', email);

        const dataObj = {
          event: '2024_form_submit',
          data: {
            eventLabel: 'Contact',
            formType: 'contact',
            marketingConsent: 'yes',
            firstName: first_name,
            lastName: last_name,
            email,
            currentPage: current_page,
            referrer: referrer,
          },
        };

        window.dataLayer.push(dataObj);
      });
    },
    enableReinitialize: true,
  });

  const {
    first_name,
    last_name,
    email,
    opt_in,
    honeypot,
    current_page,
    referrer,
    client_id,
  } = values;

  if (waiting) {
    return <LoadingSpinner />;
  }

  const { form, intro, thank_you } = fields;

  const {
    first_name: firstNameMeta,
    last_name: lastNameMeta,
    email: emailMeta,
    opt_in: optInMeta,
    gdpr: gdprMeta,
    submit: submitMeta,
  } = form;

  return (
    <FormContext.Provider value={fields.sidebar}>
      <section className="c-newsletter__form">
        <ErrorBoundary>
          {!showSuccessDialog ? (
            <div>
              <h5 className="c-newsletter__form__title">{intro.title}</h5>

              <p
                className="o-text o-text--small"
                dangerouslySetInnerHTML={{ __html: intro.content }}
              ></p>

              <p className="u-a11y">
                Required fields are followed by{' '}
                <abbr title="required" aria-label="required">
                  *
                </abbr>
                .
              </p>

              <Form
                onSubmit={(e) => {
                  e.preventDefault();

                  submitForm();
                }}
              >
                <label className="u-a11y">
                  Honeypot
                  <input
                    name="honeypot"
                    type="hidden"
                    onChange={() => setFieldValue('honeypot', true)}
                    value={honeypot}
                  />
                </label>

                <input name="referrer" value={referrer} type="hidden" />
                <input name="client_id" value={client_id} type="hidden" />
                <input name="current_page" value={current_page} type="hidden" />

                <section>
                  <fieldset className="c-newsletter__form__name">
                    <legend className="u-a11y">Your name</legend>
                    <div>
                      <TextField
                        label={
                          <>
                            {firstNameMeta.label}
                            {firstNameMeta.required && <Required />}
                          </>
                        }
                        name="first_name"
                        placeholder={firstNameMeta.placeholder}
                        onBlur={handleBlur}
                        handleChange={(value) =>
                          setFieldValue('first_name', value)
                        }
                        value={first_name}
                        validationError={
                          errors.first_name && touched.first_name
                        }
                        validationMessage={errors.first_name}
                      />
                    </div>
                    <div>
                      <TextField
                        label={
                          <>
                            {lastNameMeta.label}
                            {lastNameMeta.required && <Required />}
                          </>
                        }
                        name="last_name"
                        placeholder={lastNameMeta.placeholder}
                        onBlur={handleBlur}
                        handleChange={(value) =>
                          setFieldValue('last_name', value)
                        }
                        value={last_name}
                        validationError={errors.last_name && touched.last_name}
                        validationMessage={errors.last_name}
                      />
                    </div>
                  </fieldset>

                  <fieldset className="c-newsletter__form__email">
                    <legend className="u-a11y">Your email</legend>
                    <div>
                      <TextField
                        label={
                          <>
                            {emailMeta.label}
                            {emailMeta.required && <Required />}
                          </>
                        }
                        name="email"
                        type="email"
                        placeholder={emailMeta.placeholder}
                        onBlur={handleBlur}
                        handleChange={(value) => setFieldValue('email', value)}
                        value={email}
                        validationError={errors.email && touched.email}
                        validationMessage={errors.email}
                      />
                    </div>
                  </fieldset>

                  <div className="c-newsletter__form__gdprstatement">
                    <p className="u-weight--semibold u-mb-0">
                      {optInMeta.options[0].display}
                    </p>

                    <p
                      className="c-form__small"
                      dangerouslySetInnerHTML={{
                        __html: optInMeta.options[0].info,
                      }}
                    ></p>
                  </div>

                  <div className="c-newsletter__form__gdprstatement">
                    <p className="u-weight--semibold u-mb-0">
                      {gdprMeta.display}
                    </p>

                    <p
                      className="c-form__small"
                      dangerouslySetInnerHTML={{
                        __html: gdprMeta.info,
                      }}
                    ></p>
                  </div>

                  <button
                    type="submit"
                    className="o-btn"
                    disabled={disable(
                      errors,
                      values,
                      popupNewsletterInitialValues
                    )}
                  >
                    {submitMeta.label}
                  </button>
                </section>
              </Form>
            </div>
          ) : (
            <div
              dangerouslySetInnerHTML={{ __html: thank_you?.message || '' }}
            />
          )}
        </ErrorBoundary>
      </section>
    </FormContext.Provider>
  );
};

export default NewsletterForm;
